// src/ui/Avatar.tsx (T1): arch-topped avatars (people only) and the overlapping stack. Tints come from the
// person's order on the board (--person-1 to --person-6); the builder's team is the navy monogram, never a
// named employee. Root classes: hb-avatar, hb-avatar-stack.
import type { ComponentChildren } from "preact";
import { cx } from "./cx";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
/** 1 to 6 for people (by member order), "team" for the builder's monogram, "new" for an empty invite slot. */
export type AvatarTone = number | "team" | "new";

/** Member order (0-based) to a tint, cycling through the six person tokens. */
export function personTone(index: number): number {
  return (((Math.floor(index) % 6) + 6) % 6) + 1;
}

export type AvatarPerson = { initials: string; name: string; tone: AvatarTone };

export function Avatar(props: {
  initials: string;
  /** The person's name: the tooltip, and the accessible name when `label` is set. */
  name?: string;
  tone?: AvatarTone;
  size?: AvatarSize;
  /** Presence dot ("Here now"). */
  here?: boolean;
  /** Expose the avatar to assistive tech as an image named `name` (default: decorative, the name is nearby). */
  label?: boolean;
  class?: string;
}) {
  const tone = props.tone ?? 1;
  const toneClass = tone === "team" ? "av-ma" : tone === "new" ? "av-new" : `av-p${personTone(tone - 1)}`;
  const size = props.size ?? "md";
  return (
    <span
      class={cx("hb-avatar", "av", size !== "md" && `av-${size}`, toneClass, props.class)}
      title={props.name}
      role={props.label ? "img" : undefined}
      aria-label={props.label ? props.name : undefined}
      aria-hidden={props.label ? undefined : "true"}
    >
      {props.initials.slice(0, 2)}
      {props.here ? <span class="on" /> : null}
    </span>
  );
}

/**
 * Overlapping avatars with a 2px white ring; past `max`, a "+n" arch. `label` names the whole group for
 * assistive tech ("Loved by Dana, Chris and Ruth").
 */
export function AvatarStack(props: { people: readonly AvatarPerson[]; max?: number; size?: AvatarSize; label?: string; class?: string; children?: ComponentChildren }) {
  const max = props.max ?? 3;
  const shown = props.people.slice(0, max);
  const more = props.people.length - shown.length;
  return (
    <span class={cx("hb-avatar-stack", "stack", props.class)} role={props.label ? "img" : undefined} aria-label={props.label}>
      {shown.map((p, i) => (
        <Avatar key={i} initials={p.initials} name={p.name} tone={p.tone} size={props.size ?? "sm"} />
      ))}
      {more > 0 ? (
        <span class="more" aria-hidden="true">
          +{more}
        </span>
      ) : null}
    </span>
  );
}
